import React, { useEffect, useState } from "react";
import { Copy, Link2, Loader2, Ban, Mic } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { adminDemoLinks, createDemoLink, revokeDemoLink, pregenerateNarration, getTourVoice, setTourVoice } from "@/lib/api";

const STATUS = {
  active: ["Activo", "bg-teal-50 text-teal-700"],
  expired: ["Vencido", "bg-slate-100 text-slate-500"],
  revoked: ["Revocado", "bg-red-50 text-red-600"],
};
const shareUrl = (token) => `${window.location.origin}/demo/${token}`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("es-VE", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const errMsg = (e, f) => e?.response?.data?.detail || f;

const copy = async (token) => {
  try {
    await navigator.clipboard.writeText(shareUrl(token));
    toast.success("Enlace copiado.");
  } catch {
    toast.message(shareUrl(token));
  }
};

export const DemoLinksPanel = () => {
  const [links, setLinks] = useState([]);
  const [form, setForm] = useState({ company_key: "simpletv", label: "", days: "14" });
  const [busy, setBusy] = useState("");

  const load = () => adminDemoLinks().then((d) => setLinks(d.links || [])).catch(() => setLinks([]));
  useEffect(() => { load(); }, []);

  const create = async () => {
    setBusy("create");
    try {
      const l = await createDemoLink({ ...form, days: Number(form.days) });
      await copy(l.token);
      setForm((f) => ({ ...f, label: "" }));
      await load();
    } catch (e) {
      toast.error(errMsg(e, "No se pudo crear el enlace."));
    } finally {
      setBusy("");
    }
  };

  const revoke = async (id) => {
    await revokeDemoLink(id).catch((e) => toast.error(errMsg(e, "No se pudo revocar.")));
    await load();
  };

  const [voice, setVoice] = useState(null);
  useEffect(() => {
    getTourVoice().then(setVoice).catch(() => setVoice("browser"));
  }, []);
  const chooseVoice = async (mode) => {
    try {
      setVoice(await setTourVoice(mode));
      toast.success(mode === "ai" ? "El tour usará la voz IA." : "El tour usará la voz de la computadora.");
    } catch (e) {
      toast.error(errMsg(e, "No se pudo cambiar la voz."));
    }
  };

  const narrate = async () => {
    setBusy("narration");
    try {
      const r = await pregenerateNarration();
      toast.success(`Narración lista: ${r.files} audios.`);
    } catch (e) {
      toast.error(errMsg(e, "No se pudo generar la narración."));
    } finally {
      setBusy("");
    }
  };

  return (
    <section className="mt-14" data-testid="demo-links-panel">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow flex items-center gap-1.5"><Link2 className="h-3 w-3" /> Enlaces públicos</p>
          <h2 className="mt-2 font-display text-2xl text-ink">Compartir la demo</h2>
          <p className="mt-1 max-w-xl text-sm text-slate-600">
            El prospecto abre un reproductor de solo lectura con los 9 pasos y narración, sin iniciar sesión.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex border border-slate-300" role="radiogroup" aria-label="Voz del tour" data-testid="tour-voice-selector">
            {[["browser", "Voz de la computadora"], ["ai", "Voz IA"]].map(([m, label]) => (
              <button
                key={m}
                role="radio"
                aria-checked={voice === m}
                onClick={() => chooseVoice(m)}
                className={`px-3 py-2 text-[11px] uppercase tracking-[0.14em] transition-colors ${voice === m ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}
                data-testid={`tour-voice-${m}`}
              >
                {label}
              </button>
            ))}
          </div>
          {voice === "ai" && (
            <Button variant="outline" onClick={narrate} disabled={!!busy} className="rounded-sm text-[11px] uppercase tracking-[0.18em]" data-testid="demo-narration-btn">
              {busy === "narration" ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Mic className="mr-2 h-3.5 w-3.5" />}
              Pre-generar narración
            </Button>
          )}
        </div>
      </div>

      <div className="mt-6 grid gap-3 border border-slate-200 bg-slate-50/60 p-4 md:grid-cols-[180px_1fr_140px_auto]">
        <Select value={form.company_key} onValueChange={(v) => setForm({ ...form, company_key: v })}>
          <SelectTrigger className="rounded-sm bg-white" data-testid="demo-link-company"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="simpletv">SimpleTV</SelectItem>
            <SelectItem value="finfarma">FinFarma</SelectItem>
          </SelectContent>
        </Select>
        <Input
          value={form.label}
          onChange={(e) => setForm({ ...form, label: e.target.value })}
          placeholder="Etiqueta (p. ej. Evento Caracas · Banco X)"
          className="rounded-sm bg-white"
          data-testid="demo-link-label"
        />
        <Select value={form.days} onValueChange={(v) => setForm({ ...form, days: v })}>
          <SelectTrigger className="rounded-sm bg-white" data-testid="demo-link-days"><SelectValue /></SelectTrigger>
          <SelectContent>
            {["1", "7", "14", "30", "90"].map((d) => <SelectItem key={d} value={d}>{d} {d === "1" ? "día" : "días"}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={create} disabled={!!busy} className="rounded-sm bg-slate-900 text-[11px] uppercase tracking-[0.18em] text-white hover:bg-slate-800" data-testid="demo-link-create-btn">
          {busy === "create" ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Link2 className="mr-2 h-3.5 w-3.5" />} Crear enlace
        </Button>
      </div>

      <div className="mt-4 overflow-x-auto border border-slate-200" data-testid="demo-links-table">
        <table className="w-full min-w-[760px] text-sm">
          <thead className="bg-slate-50 text-left text-[10px] uppercase tracking-[0.16em] text-slate-500">
            <tr>{["Empresa", "Etiqueta", "Creado", "Vence", "Aperturas", "Estado", ""].map((h) => <th key={h} className="px-4 py-2 font-medium">{h}</th>)}</tr>
          </thead>
          <tbody>
            {!links.length && <tr><td colSpan={7} className="px-4 py-6 text-center text-slate-400">Aún no hay enlaces.</td></tr>}
            {links.map((l) => <LinkRow key={l.id} l={l} onRevoke={revoke} />)}
          </tbody>
        </table>
      </div>
    </section>
  );
};

const LinkRow = ({ l, onRevoke }) => {
  const [label, cls] = STATUS[l.status];
  return (
    <tr className="border-t border-slate-100" data-testid={`demo-link-row-${l.id}`}>
      <td className="px-4 py-3 font-medium text-slate-900">{l.company}</td>
      <td className="px-4 py-3 text-slate-600">{l.label || "—"}</td>
      <td className="px-4 py-3 text-slate-600">{fmtDate(l.created_at)}</td>
      <td className="px-4 py-3 text-slate-600">{fmtDate(l.expires_at)}</td>
      <td className="px-4 py-3 text-slate-900" data-testid={`demo-link-opens-${l.id}`}>
        {l.opens}{l.last_opened_at && <span className="block text-[11px] text-slate-400">últ. {fmtDate(l.last_opened_at)}</span>}
      </td>
      <td className="px-4 py-3"><span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.16em] ${cls}`} data-testid={`demo-link-status-${l.id}`}>{label}</span></td>
      <td className="px-4 py-3">
        <div className="flex justify-end gap-2">
          <button onClick={() => copy(l.token)} className="text-slate-500 hover:text-slate-900" title="Copiar enlace" data-testid={`demo-link-copy-${l.id}`}><Copy className="h-4 w-4" /></button>
          {l.status === "active" && (
            <button onClick={() => onRevoke(l.id)} className="text-red-500 hover:text-red-700" title="Revocar" data-testid={`demo-link-revoke-${l.id}`}><Ban className="h-4 w-4" /></button>
          )}
        </div>
      </td>
    </tr>
  );
};
